import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

export const fetchIndicators = (timeframe = '5m') => api.get(`/api/indicators?timeframe=${timeframe}`);
export const fetchIndicatorHistory = (limit = 50) => api.get(`/api/indicators/history?limit=${limit}`);
export const fetchIndices = () => api.get('/api/indices');
export const fetchIndexContributors = (index = 'Nifty') => api.get(`/api/index-contributors/${index}`);
export const fetchSystemStatus = () => api.get('/api/system/status');
export const fetchHistorical = (date, timeframe = '5m') => api.get(`/api/historical/${date}?timeframe=${timeframe}`);
export const fetchStrategies = () => api.get('/api/strategies');
export const fetchStrategy = (name) => api.get(`/api/strategies/${name}`);
export const fetchTrades = (strategy = '', limit = 50, todayOnly = false) =>
  api.get(`/api/trades?strategy=${strategy}&limit=${limit}&today_only=${todayOnly}`);
export const fetchTradeHistory = (params = {}) => api.get('/api/trade-history', { params });
export const fetchBacktest = (date) => api.get(`/api/strategies/backtest/${date}`, { timeout: 120000 });
export const fetchBacktestRange = (from, to) => api.get(`/api/strategies/backtest-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestStraddle100 = (date) => api.get(`/api/strategies/backtest-straddle100/${date}`, { timeout: 120000 });
export const fetchBacktestStraddle100Range = (from, to) => api.get(`/api/strategies/backtest-straddle100-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestThetaExpiry = (date) => api.get(`/api/strategies/backtest-theta-expiry/${date}`, { timeout: 120000 });
export const fetchBacktestThetaExpiryRange = (from, to) => api.get(`/api/strategies/backtest-theta-expiry-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestStraddle50Expiry = (date) => api.get(`/api/strategies/backtest-straddle50-expiry/${date}`, { timeout: 120000 });
export const fetchBacktestStraddle50ExpiryRange = (from, to) => api.get(`/api/strategies/backtest-straddle50-expiry-range/${from}/${to}`, { timeout: 600000 });

// SENSEX backtests
export const fetchBacktestThetaSensexWed = (date) => api.get(`/api/strategies/backtest-theta-sensex-wed/${date}`, { timeout: 120000 });
export const fetchBacktestThetaSensexWedRange = (from, to) => api.get(`/api/strategies/backtest-theta-sensex-wed-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestStraddle300SensexWed = (date) => api.get(`/api/strategies/backtest-straddle300-sensex-wed/${date}`, { timeout: 120000 });
export const fetchBacktestStraddle300SensexWedRange = (from, to) => api.get(`/api/strategies/backtest-straddle300-sensex-wed-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestThetaSensexExpiry = (date) => api.get(`/api/strategies/backtest-theta-sensex-expiry/${date}`, { timeout: 120000 });
export const fetchBacktestThetaSensexExpiryRange = (from, to) => api.get(`/api/strategies/backtest-theta-sensex-expiry-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestStraddle100SensexExpiry = (date) => api.get(`/api/strategies/backtest-straddle100-sensex-expiry/${date}`, { timeout: 120000 });
export const fetchBacktestStraddle100SensexExpiryRange = (from, to) => api.get(`/api/strategies/backtest-straddle100-sensex-expiry-range/${from}/${to}`, { timeout: 600000 });
export const fetchBacktestRollingStrangle = (date) => api.get(`/api/strategies/backtest-rolling-strangle/${date}`, { timeout: 120000 });
export const fetchBacktestRollingStrangleRange = (from, to) => api.get(`/api/strategies/backtest-rolling-strangle-range/${from}/${to}`, { timeout: 600000 });

// Broker accounts
export const fetchAccounts = () => api.get('/api/accounts');
export const fetchAccountFunds = () => api.get('/api/accounts/funds');
export const createAccount = (data) => api.post('/api/accounts', data, { timeout: 60000 });
export const updateAccount = (id, data) => api.put(`/api/accounts/${id}`, data);
export const deleteAccount = (id) => api.delete(`/api/accounts/${id}`);
export const testAccountConnection = (data) => api.post('/api/accounts/connect', data);
export const setDataFeedAccount = (id) => api.post(`/api/accounts/${id}/set-data-feed`);
export const reconnectAccount = (id) => api.post(`/api/accounts/${id}/reconnect`);
export const getAngelOneLoginUrl = () => api.get('/api/auth/angelone/login-url');
export const getAngelOneDefaultKey = () => api.get('/api/auth/angelone/default-key');
export const getKiteLoginUrl = (accountId) => api.get('/api/auth/kite/login-url', { params: accountId ? { account_id: accountId } : {} });

// Strategy configuration
export const fetchStrategyConfigs = () => api.get('/api/config/strategies');
export const createStrategyConfig = (data) => api.post('/api/config/strategies', data);
export const updateStrategyConfig = (id, data) => api.put(`/api/config/strategies/${id}`, data);
export const deleteStrategyConfig = (id) => api.delete(`/api/config/strategies/${id}`);

// Day-type classification (observation-only)
export const fetchClassificationLatest = () => api.get('/api/classification/latest');
export const fetchClassificationToday = () => api.get('/api/classification/today');
export const fetchClassificationByDate = (date) => api.get(`/api/classification/by-date/${date}`);
export const fetchClassificationHistory = (days = 30) => api.get(`/api/classification/history?days=${days}`);
export const triggerClassification = () => api.post('/api/classification/trigger');


// Proxy management
export const getProxyStatus = (accountId) => api.get(`/api/proxy/status/${accountId}`);
export const provisionProxy = (accountId) => api.post(`/api/proxy/provision/${accountId}`);
export const deleteProxy = (accountId) => api.delete(`/api/proxy/${accountId}`);
export const listProxies = () => api.get('/api/proxy/list');

// WebSocket feed
export const getWsFeedStatus = () => api.get('/api/ws-feed/status');
export const restartWsFeed = () => api.post('/api/ws-feed/restart');

// Strategy metadata
export const fetchStrategyMeta = () => api.get('/api/strategy-meta');

// Manual single-leg trade
export const fetchNearbyStrikes = (index = 'Nifty', count = 10) =>
  api.get(`/api/manual/nearby-strikes?index=${index}&count=${count}`);
export const manualEntry = (data) => api.post('/api/manual/entry', data);
export const manualExit = (data) => api.post('/api/manual/exit', data);

// Open positions
export const fetchPositions = () => api.get('/api/positions');
export const exitPositions = (data) => api.post('/api/positions/exit', data);

// Option chain
export const fetchOptionChain = (index = 'Nifty', count = 20) =>
  api.get(`/api/option-chain?index=${index}&count=${count}`);

// Unified backtest
export const fetchBacktestUnified = (params) =>
  api.post('/api/backtest', params, { timeout: 600000 });

// Straddle chart
export const fetchStraddleChart = (strategyName) =>
  api.get(`/api/straddle-chart/${strategyName}`);

// Combined premium chart
export const fetchCombinedPremiumChart = () =>
  api.get('/api/combined-premium-chart');

// Premium history (time-series for line charts)
export const fetchPremiumHistory = () =>
  api.get('/api/premium-history');

// EOD P&L snapshot
export const triggerEodSnapshot = () =>
  api.post('/api/eod-snapshot');

export default api;
